import { Link } from '@tanstack/react-router'

import { navigationItemForLeaf } from '@/app/routes/area-layout'
import type { HomeWidgetProps } from '@/lib/home-widgets'
import { usePermissions } from '@/lib/use-permissions'
import { HomeWidgetEmpty, HomeWidgetLoading } from '@/ui/home-widget'
import { RelativeTime } from '@/ui/relative-time'

import { useRecentPages } from './use-recent-pages'

const VISIBLE_LIMIT = 6

// ponytail: ages render once and don't tick while the board stays open; add a timer if stale ages get noticed.
export function RecentlyVisitedWidget({ entityId }: HomeWidgetProps) {
  const { can, ready } = usePermissions()
  const pages = useRecentPages()
  if (!ready) return <HomeWidgetLoading />

  const rows = pages.flatMap((page) => {
    if (page.entityId !== entityId) return []
    const item = navigationItemForLeaf(page.to)
    if (item?.to !== page.to || (item.permission && !can(item.permission))) return []
    return [{ item, visitedAt: page.visitedAt }]
  }).slice(0, VISIBLE_LIMIT)

  if (rows.length === 0) return <HomeWidgetEmpty>Pages you open will show up here.</HomeWidgetEmpty>

  return (
    <ul className="-mx-2 space-y-0.5">
      {rows.map(({ item, visitedAt }) => {
        const Icon = item.icon
        return (
          <li key={item.to}>
            <Link
              to={item.to}
              params={{ entityId } as never}
              className="hover:bg-muted focus-visible:ring-foreground flex items-center gap-3 rounded-md px-2 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-inset"
            >
              <Icon aria-hidden className="text-muted-foreground size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate" title={item.label}>{item.label}</span>
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                <RelativeTime iso={visitedAt} compact />
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
